import {
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  type DragEvent,
  useId,
  useRef,
  useState,
} from "react";
import { twMerge } from "tailwind-merge";
import { useTranslation } from "react-i18next";
import { UploadCloud01, XClose } from "untitledui-js/react";

import { Label } from "./Label";
import {
  ErrorMessage,
  INVALID_FILE_TYPE_ERROR_KEY,
  type ErrorKey,
} from "./ErrorMessage";
import { Description } from "./Description";

const MIME_LABELS: Record<string, string> = {
  "application/pdf": "PDF",
  "application/msword": "DOC",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "DOCX",
};

const mimeLabel = (mime: string) =>
  MIME_LABELS[mime] ?? mime.split("/")[1]?.toUpperCase() ?? mime;

interface UploadFileProps
  extends Omit<ComponentPropsWithoutRef<"input">, "type"> {
  label: string;
  description?: string;
  error?: ErrorKey;
  allowedMimes?: string[];
}

export const UploadFile = ({
  label,
  description,
  error,
  id,
  required,
  multiple,
  accept,
  allowedMimes,
  onChange,
  "aria-describedby": ariaDescribedBy,
  ...uploadProps
}: UploadFileProps) => {
  const { t } = useTranslation();

  const generatedId = useId();
  const uploadId = id ?? generatedId;
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [hasInvalidFile, setHasInvalidFile] = useState(false);

  /**
   * `accept` only filters the native OS picker (best-effort, skippable via
   * "All Files", and ignored on drag-and-drop) — `allowedMimes` is enforced
   * here in JS so disallowed files are always stripped regardless of how
   * they were selected.
   *
   * @param incoming The array of incoming files to be filtered.
   * @returns An object containing `valid` and `rejected` arrays of files.
   */
  const filterByMime = (incoming: File[]) => {
    if (!allowedMimes || allowedMimes.length === 0) {
      return { valid: incoming, rejected: [] as File[] };
    }

    return {
      valid: incoming.filter((file) => allowedMimes.includes(file.type)),
      rejected: incoming.filter((file) => !allowedMimes.includes(file.type)),
    };
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const incoming = Array.from(e.target.files ?? []);
    const { valid, rejected } = filterByMime(incoming);

    if (rejected.length > 0 && inputRef.current) {
      const dataTransfer = new DataTransfer();
      valid.forEach((file) => dataTransfer.items.add(file));
      try {
        inputRef.current.files = dataTransfer.files;
      } catch {
        // ignore, fall through with whatever files remain on the input
      }
    }

    setFiles(valid);
    setHasInvalidFile(rejected.length > 0);
    onChange?.(e);
  };

  const openFileDialog = () => {
    inputRef.current?.click();
  };

  /**
   * Removes a file from the list of selected files by index.
   * This is done by creating a new DataTransfer object and adding
   * all files except the one to be removed, then updating the
   * input's files property and dispatching a change event.
   *
   * @param index The index of the file to be removed from the list of selected files.
   */
  const removeFile = (index: number) => {
    if (!inputRef.current) return;

    const dataTransfer = new DataTransfer();
    files.forEach((file, i) => {
      if (i !== index) dataTransfer.items.add(file);
    });

    try {
      inputRef.current.files = dataTransfer.files;
    } catch {
      return;
    }

    inputRef.current.dispatchEvent(new Event("change", { bubbles: true }));
  };

  const formatFileSize = (bytes: number) =>
    bytes < 1024 * 1024
      ? `${(bytes / 1024).toFixed(1)} KB`
      : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

  const handleDragOver = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDrop = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (!inputRef.current || droppedFiles.length === 0) return;

    const dataTransfer = new DataTransfer();
    if (multiple) {
      Array.from(droppedFiles).forEach((file) => dataTransfer.items.add(file));
    } else {
      dataTransfer.items.add(droppedFiles[0]);
    }

    try {
      inputRef.current.files = dataTransfer.files;
    } catch {
      return;
    }

    inputRef.current.dispatchEvent(new Event("change", { bubbles: true }));
  };

  const displayError =
    error ?? (hasInvalidFile ? INVALID_FILE_TYPE_ERROR_KEY : undefined);
  const acceptAttr = accept ?? allowedMimes?.join(",");
  const allowedTypesHint = accept ?? allowedMimes?.map(mimeLabel).join(", ");

  const hintText =
    [allowedTypesHint, multiple && t("Vacancies.form.upload.multipleHint")]
      .filter(Boolean)
      .join(" · ") || t("Vacancies.form.upload.anyFileHint");

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={uploadId} label={label} required={required} />

      <button
        type="button"
        aria-label={label}
        aria-describedby={ariaDescribedBy || undefined}
        onClick={openFileDialog}
        onDragOver={handleDragOver}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={twMerge(
          "flex min-h-28 w-full flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-5 py-5 text-center transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2",
          displayError
            ? "cursor-pointer border-accent_three-600 hover:border-accent_three-700"
            : "",
          isDragging
            ? "cursor-pointer border-primary-500 bg-primary-50"
            : "cursor-pointer border-secondary-100 hover:border-secondary-200 hover:bg-secondary-50"
        )}
      >
        <UploadCloud01
          aria-hidden="true"
          className={twMerge(
            "size-6",
            displayError ? "stroke-accent_three-600" : "",
            isDragging ? "stroke-primary-500" : "stroke-secondary-700"
          )}
        />

        <div>
          <p
            className={twMerge(
              "font-sans text-sm font-semibold",
              isDragging ? "text-primary-600" : "text-tertiary-900"
            )}
          >
            {isDragging
              ? t("Vacancies.form.upload.dropHint")
              : t("Vacancies.form.upload.clickOrDragHint")}
          </p>
          <p className="font-sans text-xs font-medium text-tertiary-500">
            {hintText}
          </p>
        </div>
      </button>

      {files.length > 0 && (
        <ul className="flex flex-col gap-1" aria-live="polite">
          {files.map((file, i) => (
            <li
              key={`${file.name}-${i}`}
              className="flex items-center gap-2 rounded-md border border-secondary-100 bg-white px-3 py-2"
            >
              <UploadCloud01
                aria-hidden="true"
                className="size-3.5 shrink-0 stroke-secondary-700"
              />
              <span className="min-w-0 flex-1 truncate font-sans text-sm text-tertiary-900">
                {file.name}
              </span>
              <span className="shrink-0 font-mono text-xs text-tertiary-500">
                {formatFileSize(file.size)}
              </span>
              <button
                type="button"
                aria-label={t("Vacancies.form.upload.removeFile", {
                  filename: file.name,
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(i);
                }}
                className="shrink-0 cursor-pointer rounded-sm stroke-secondary-700 hover:stroke-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:outline-none"
              >
                <XClose aria-hidden="true" className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        {...uploadProps}
        ref={inputRef}
        id={uploadId}
        type="file"
        multiple={multiple}
        accept={acceptAttr}
        required={required}
        onChange={handleChange}
        aria-hidden="true"
        tabIndex={-1}
        className="sr-only"
      />

      {displayError && <ErrorMessage error={displayError} />}
      {description && !displayError && (
        <Description description={description} />
      )}
    </div>
  );
};
